type Program = {
  startTime: string
  endTime: string
  title: string
  src: string | string[]
  cover?: string
}

type DiaSemana =
  | "domingo"
  | "lunes"
  | "martes"
  | "miercoles"
  | "jueves"
  | "viernes"
  | "sabado"

/* =========================
   TIME CACHE (OPTIMIZED PARSER)
========================= */
const timeCache = new Map<string, number>()

export function timeToMinutes(t: string): number {
  const cached = timeCache.get(t)
  if (cached !== undefined) return cached

  const [h, m] = t.split(":").map(Number)
  const value = (h || 0) * 60 + (m || 0)

  timeCache.set(t, value)
  return value
}

/* =========================
   CURRENT PROGRAM
========================= */
export function getCurrentRadioProgram(
  radio: Record<DiaSemana, Program[]>,
  dia: DiaSemana,
  now = new Date()
): Program | null {
  const list = radio[dia]
  if (!list?.length) return null

  const nowMinutes = now.getHours() * 60 + now.getMinutes()

  for (let i = 0; i < list.length; i++) {
    const p = list[i]

    const start = timeToMinutes(p.startTime)
    const end = timeToMinutes(p.endTime)

    if (nowMinutes >= start && nowMinutes < end) {
      return p
    }
  }

  return null
}

/* =========================
   OFFSET (SYNC ENGINE CORE)
========================= */
export function getProgramOffsetSeconds(
  program: Program,
  now = new Date()
): number {
  const startMinutes = timeToMinutes(program.startTime)

  const nowMinutes = now.getHours() * 60 + now.getMinutes()
  const seconds = now.getSeconds()

  return (nowMinutes - startMinutes) * 60 + seconds
}

/* =========================
   PROGRAM DURATION
========================= */
export function getProgramDurationSeconds(program: Program): number {
  const start = timeToMinutes(program.startTime)
  const end = timeToMinutes(program.endTime)

  return Math.max(0, (end - start) * 60)
}

/* =========================
   NORMALIZE SRC (QUEUE SAFE)
========================= */
export function getProgramSrc(program: Program): string[] {
  if (!program?.src) return []

  return Array.isArray(program.src)
    ? program.src.filter(Boolean)
    : [program.src]
}

/* =========================
   RANGE (DEBUG / UI)
========================= */
export function getProgramRange(program: Program) {
  return {
    startMinutes: timeToMinutes(program.startTime),
    endMinutes: timeToMinutes(program.endTime),
  }
}

/* =========================
   ACTIVE CHECK
========================= */
export function isProgramActive(program: Program | null): boolean {
  return program !== null
}